import { ErpDiv, ErpLabelRaw, ErpSpan } from './ErpHtml';
import React, { useMemo } from 'react';
import { ErpFormInvalidFeedback } from './ErpFormInvalidFeedback';
import { ErpFormText } from './ErpFormText';

export interface ErpFormGroupProps {
  label?: React.ReactNode;
  labelFor?: string;
  labelCols?: string | number | boolean;
  labelColsSm?: string | number | boolean;
  labelColsMd?: string | number | boolean;
  labelColsLg?: string | number | boolean;
  labelColsXl?: string | number | boolean;
  labelAlign?: 'start' | 'center' | 'end' | 'left' | 'right';
  labelAlignSm?: string;
  labelAlignMd?: string;
  labelAlignLg?: string;
  labelAlignXl?: string;
  labelClass?: string;
  labelSrOnly?: boolean;
  labelVisuallyHidden?: boolean;
  description?: React.ReactNode;
  invalidFeedback?: React.ReactNode;
  validFeedback?: React.ReactNode;
  state?: boolean | null;
  required?: boolean;
  contentCols?: string | number | boolean;
  contentColsSm?: string | number;
  contentColsMd?: string | number;
  contentColsLg?: string | number;
  contentColsXl?: string | number;
  feedbackAriaLive?: 'polite' | 'assertive' | 'off';
  children?: React.ReactNode;
  prepend?: React.ReactNode;
  append?: React.ReactNode;
  className?: string;
  error?: React.ReactNode;
  [key: string]: any;
}

const ErpFormGroupBase: React.FC<ErpFormGroupProps> = ({
  label,
  labelFor,
  labelCols,
  labelColsSm,
  labelColsMd,
  labelColsLg,
  labelColsXl,
  labelAlign,
  labelAlignSm,
  labelAlignMd,
  labelAlignLg,
  labelAlignXl,
  labelClass,
  labelSrOnly = false,
  labelVisuallyHidden = false,
  description,
  invalidFeedback,
  error,
  validFeedback,
  state = null,
  required = false,
  contentCols,
  contentColsSm,
  contentColsMd,
  contentColsLg,
  contentColsXl,
  feedbackAriaLive = 'assertive',
  children,
  prepend,
  append,
  className
}) => {
  // `label`/`description` are ReactNodes, so a falsy-but-valid value (0, '')
  // must still count as present.
  const hasLabel = label !== undefined && label !== null;
  const hasDescription = description !== undefined && description !== null;

  const showInvalid = !!error || (state === false && !!invalidFeedback);
  const invalidMessage = error || invalidFeedback;
  const showValid = !showInvalid && state === true && !!validFeedback;

  const horizontal =
    (labelCols !== undefined && labelCols !== false) ||
    (labelColsSm !== undefined && labelColsSm !== false) ||
    (labelColsMd !== undefined && labelColsMd !== false) ||
    (labelColsLg !== undefined && labelColsLg !== false) ||
    (labelColsXl !== undefined && labelColsXl !== false) ||
    (contentCols !== undefined && contentCols !== false);

  const colClass = (v: string | number | boolean | undefined, prefix?: string): string => {
    if (v === undefined || v === false) return '';
    // Only emit a class Tailwind actually generates, otherwise the grid
    // silently falls back to a full-width/auto cell.
    if (v === true) {
      const autoClass = 'col-auto';
      return prefix ? `${prefix}:${autoClass}` : autoClass;
    }
    const raw = String(v);
    const valid = /^(1[0-2]|[1-9]|auto)$/.test(raw) ? raw : 'auto';
    const spanClass = `col-span-${valid}`;
    return prefix ? `${prefix}:${spanClass}` : spanClass;
  };

  const labelColClass = [
    colClass(labelCols),
    colClass(labelColsSm, 'sm'),
    colClass(labelColsMd, 'md'),
    colClass(labelColsLg, 'lg'),
    colClass(labelColsXl, 'xl'),
  ].filter(Boolean).join(' ');

  const contentColsClass = [
    colClass(contentCols),
    colClass(contentColsSm, 'sm'),
    colClass(contentColsMd, 'md'),
    colClass(contentColsLg, 'lg'),
    colClass(contentColsXl, 'xl'),
  ].filter(Boolean).join(' ');

  const labelAlignClass = useMemo(() => {
    const formatAlign = (val?: string) => {
      if (!val) return '';
      return val === 'left' ? 'start' : val === 'right' ? 'end' : val;
    };
    return [
      labelAlign ? `text-${formatAlign(labelAlign)}` : '',
      labelAlignSm ? `sm:text-${formatAlign(labelAlignSm)}` : '',
      labelAlignMd ? `md:text-${formatAlign(labelAlignMd)}` : '',
      labelAlignLg ? `lg:text-${formatAlign(labelAlignLg)}` : '',
      labelAlignXl ? `xl:text-${formatAlign(labelAlignXl)}` : '',
    ].filter(Boolean).join(' ');
  }, [labelAlign, labelAlignSm, labelAlignMd, labelAlignLg, labelAlignXl]);

  return (
    <ErpDiv className={['min-w-0 mb-4', horizontal && 'grid grid-cols-12 gap-x-4 gap-y-2 items-center', className].filter(Boolean).join(' ')}>
      {hasLabel && !labelSrOnly && !labelVisuallyHidden ? (
        <ErpLabelRaw
          htmlFor={labelFor}
          className={['text-sm font-medium leading-relaxed peer-disabled:cursor-not-allowed peer-disabled:opacity-70', labelClass, horizontal ? labelColClass || 'col-span-12' : '', labelAlignClass].filter(Boolean).join(' ')}
        >
          {label}
          {required && <ErpSpan className="text-red-500 ml-1">*</ErpSpan>}
        </ErpLabelRaw>
      ) : hasLabel && (labelSrOnly || labelVisuallyHidden) ? (
        <ErpLabelRaw htmlFor={labelFor} className="sr-only">
          {label}
        </ErpLabelRaw>
      ) : (!label && required) ? (
        <ErpLabelRaw className={['text-sm font-medium leading-none', labelClass, horizontal ? labelColClass || 'col-span-12' : '', labelAlignClass].filter(Boolean).join(' ')}>
          <ErpSpan className="text-red-500">*</ErpSpan>
        </ErpLabelRaw>
      ) : null}
      {/* className belongs to the outer wrapper only — merging it here too
          applied every consumer utility at two DOM levels. */}
      <ErpDiv className={['min-w-0', horizontal ? contentColsClass || 'col-span-12' : ''].filter(Boolean).join(' ')}>
        {prepend && <ErpDiv className="flex mb-1 gap-2 items-center">{prepend}</ErpDiv>}
        {children}
        {append && <ErpDiv className="flex mt-1 gap-2 items-center">{append}</ErpDiv>}

        {showInvalid && (
          <ErpFormInvalidFeedback state={false} forceShow role="alert" aria-live={feedbackAriaLive}>
            {invalidMessage}
          </ErpFormInvalidFeedback>
        )}

        {showValid && (
          <ErpDiv className="text-[0.8rem] font-medium text-green-600 mt-1" aria-live={feedbackAriaLive}>
            {validFeedback}
          </ErpDiv>
        )}

        {hasDescription && (
          <ErpFormText>
            {description}
          </ErpFormText>
        )}
      </ErpDiv>
    </ErpDiv>
  );
};

export const ErpFormGroup = React.memo(ErpFormGroupBase);
